import { Alert, Checkbox, Col, Form, Row, Select, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  AddressAutoComplete,
  type SuggestionAddress
} from "../../../../components/AutoComplete/AddressAutoComplete.tsx"
import { ZipLookupHint } from "../../../../components/AutoComplete/ZipLookupHint.tsx"
import { ResolvedSelect } from "../../../../components/Select/ResolvedSelect.tsx"
import { ProjectTypeSelect } from "../../../../components/Shared/ProjectTypeSelect.tsx"
import { placeholders } from "../../../../constants/placeholders"
import { useZipLookup } from "../../../../hooks/use-zip-lookup.ts"
import {
  optionRequiresDetail,
  pickableOptions
} from "../../../../utils/select/managed-list-options.ts"
import { validateZipCode } from "../../../../utils/validation/zip-code.ts"
import {
  LEAD_SOURCE_NONE,
  withNoneSourceOption
} from "../../../Leads/constants/lead-source-none.ts"
import type { LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import {
  ARCHITECT_NAME_MAX_LENGTH,
  isLeadSourceArchitect
} from "../../constants/lead-source-architect.ts"
import { applyAddressSuggestion } from "../../helpers/address-suggestion.ts"
import type { LeadFormData } from "../components/lead-form-data.ts"
import type { LinkedCustomerAddress } from "./linked-customer.ts"
import { TextArea } from "../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const SOURCE_COMMENT_MAX_LENGTH = 255
const DESCRIPTION_MAX_LENGTH = 500

interface LeadWizardProjectStepProps {
  form: FormInstance<LeadFormData>
  options: LeadsFilterOptions
  customerAddress: LinkedCustomerAddress | null
}

export function LeadWizardProjectStep({
  form,
  options,
  customerAddress
}: LeadWizardProjectStepProps) {
  const { t } = useTranslation()

  const source = Form.useWatch("source", form)
  const showArchitectName = isLeadSourceArchitect(source)
  const showSourceComment = optionRequiresDetail(source, options?.["source"])

  const sourceOptions = useMemo(
    () => pickableOptions(withNoneSourceOption(options?.["source"])),
    [options]
  )
  const knownStates = useMemo(
    () => (options?.["state"] ?? []).map((option) => String(option?.value)),
    [options]
  )

  const { lookupZip, chooseMatch, suppressLookupFor, status } = useZipLookup({
    knownStates,
    onFill: (fill) => form.setFieldsValue(fill)
  })

  const onAddressSelect = (suggestion: SuggestionAddress) => {
    const applied = applyAddressSuggestion(suggestion, knownStates)
    suppressLookupFor(applied.zip_code)
    form.setFieldsValue(applied)
  }

  const onUseCustomerAddress = (checked: boolean) => {
    if (!checked || !customerAddress) return

    suppressLookupFor(customerAddress.zip_code)
    form.setFieldsValue({
      address: customerAddress.address,
      address_2: customerAddress.address_2,
      city: customerAddress.city,
      state: customerAddress.state,
      zip_code: customerAddress.zip_code
    })
  }

  return (
    <>
      <Typography.Title level={5}>
        {t("leadWizard.project.sectionTitle")}
      </Typography.Title>
      <Typography.Paragraph type={"secondary"}>
        {t("leadWizard.project.introduction")}
      </Typography.Paragraph>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.project.name")}
            name={"lead_name"}
          >
            <TextInput placeholder={t("leadWizard.project.namePlaceholder")} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.project.type")}
            name={"project_type"}
          >
            <ProjectTypeSelect
              placeholder={t("leadWizard.project.typePlaceholder")}
            />
          </Form.Item>
        </Col>
      </Row>

      {!!customerAddress && (
        <Form.Item<LeadFormData>
          name={"use_customer_address"}
          valuePropName={"checked"}
        >
          <Checkbox
            onChange={(event) => onUseCustomerAddress(event.target.checked)}
          >
            {t("leadWizard.project.sameAsCustomer")}
          </Checkbox>
        </Form.Item>
      )}

      <Form.Item<LeadFormData>
        label={t("leadWizard.project.addressTitle")}
        name={"address"}
      >
        <AddressAutoComplete
          onSelect={onAddressSelect}
          placeholder={t("leadWizard.project.addressPlaceholder")}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={10}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.project.city")}
            name={"city"}
          >
            <TextInput placeholder={t("leadWizard.project.cityPlaceholder")} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.project.state")}
            name={"state"}
            normalize={(value: string) => value ?? null}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp={"label"}
              placeholder={t("leadWizard.project.statePlaceholder")}
              options={options?.state}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.project.zipCode")}
            name={"zip_code"}
            rules={[{ validator: validateZipCode }]}
          >
            <TextInput
              disableAutoCapitalize
              placeholder={placeholders.zipCode}
              onChange={(event) => lookupZip(event.target.value)}
            />
          </Form.Item>
          <ZipLookupHint status={status} onChoose={chooseMatch} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item<LeadFormData>
            label={t("leadWizard.project.source")}
            name={"source"}
            normalize={(value: string) => value ?? LEAD_SOURCE_NONE}
          >
            <ResolvedSelect
              allowClear
              showSearch
              optionFilterProp={"label"}
              options={sourceOptions}
              placeholder={t("leadWizard.project.sourcePlaceholder")}
            />
          </Form.Item>
        </Col>
        {showSourceComment && (
          <Col xs={24} sm={12}>
            <Form.Item<LeadFormData>
              label={t("leadWizard.project.sourceComment")}
              name={"source_comment"}
              rules={[
                {
                  max: SOURCE_COMMENT_MAX_LENGTH,
                  message: t("leadWizard.project.sourceCommentTooLong")
                }
              ]}
            >
              <TextInput
                maxLength={SOURCE_COMMENT_MAX_LENGTH}
                placeholder={t("leadWizard.project.sourceCommentPlaceholder")}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      {showArchitectName && (
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item<LeadFormData>
              label={t("leadWizard.project.architectName")}
              name={"architect_name"}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("leadWizard.project.architectNameRequired")
                },
                {
                  max: ARCHITECT_NAME_MAX_LENGTH,
                  message: t("leadWizard.project.architectNameTooLong")
                }
              ]}
            >
              <TextInput
                maxLength={ARCHITECT_NAME_MAX_LENGTH}
                placeholder={t("leadWizard.project.architectNamePlaceholder")}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Form.Item<LeadFormData>
        label={t("leadWizard.project.description")}
        name={"comments"}
      >
        <TextArea
          rows={4}
          maxLength={DESCRIPTION_MAX_LENGTH}
          showCount
          placeholder={t("leadWizard.project.descriptionPlaceholder")}
        />
      </Form.Item>

      <Alert
        type={"info"}
        showIcon
        message={t("leadWizard.project.optionalHint")}
        description={t("leadWizard.project.optionalHintDescription")}
      />
    </>
  )
}
